import render from 'dom-serializer';
import parse from 'html-react-parser';
import dompurify from 'isomorphic-dompurify';
import { PropTypes } from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { dompurifyConfig } from '../../../utils/utils';
import FootnoteLink from '../FootnoteLink';
import AuthorQuote from './AuthorQuote';
import { parseYoutubeTimecode } from '../../../utils/parsing';
import {
  isAccordeonDomNode,
  isAuthorQuote,
  isEcoWebInlineImage,
  isLinkBoxSticky,
  isScript,
  isShowMoreDomNode,
  nodeToEcoWebInlineImage,
} from './ck-utils';
import InformationDeroulante from './InformationDeroulante';
import InlineScript from './InlineScript';
import MoreDetails from './MoreDetails';

const CkEditor = ({ html, lang, elemAnchor }) => {
  const { t } = useTranslation();
  const [isClient, setIsClient] = React.useState(false);
  React.useEffect(() => {
    setIsClient(true);

    return () => {};
  }, []);

  const sanitizer = dompurify.sanitize;
  html = sanitizer(html, dompurifyConfig);

  const getDisplayLang = () => {
    if (lang) {
      return lang.toLowerCase();
    }
    return undefined;
  };

  const domElementHasClass = (el, clazz) => {
    return el?.attribs?.class === clazz;
  };

  const getDomElementText = (el) => {
    try {
      return render(el.childNodes);
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  let accIdx = 0;
  let stickyBoxindex = 0;

  const nodeToInformationDeroulante = (domNode) => {
    accIdx++;
    let title, content;
    domNode.childNodes.forEach((el) => {
      if (domElementHasClass(el, 'accordeon-title')) {
        title = getDomElementText(el);
      } else if (domElementHasClass(el, 'accordeon-content')) {
        content = getDomElementText(el);
      }
    });
    return <InformationDeroulante content={content} key={'accordeon-' + accIdx} title={title} titleBalise="div" />;
  };

  const getStickyInnerHtml = (domNode) => {
    if (domNode) {
      try {
        return render(domNode);
      } catch (e) {
        console.error(e);
      }
    }
    return '';
  };

  const nodeToLinkBoxSticky = (domNode) => {
    if (domNode.children && domNode.children.length > 1) {
      domNode.children.splice(1, 1);
    }
    stickyBoxindex++;
    return (
      <div
        className="ckContent ckContent-linkbox-sticky"
        dangerouslySetInnerHTML={{ __html: getStickyInnerHtml(domNode) }}
        key={'sticky-box-' + stickyBoxindex}
      ></div>
    );
  };

  const nodeToAuthorQuote = (domNode) => {
    const dta = ['job-position', 'job-position_detail', 'text', 'name'];
    const pp = ['jobPosition', 'jobPositionDetail', 'text', 'name'];
    const quote = {};

    const visitNode = (n) => {
      const cls = n?.attribs?.class;
      if (n?.name === 'figure' && n?.attribs?.class === 'image') {
        quote.imageLeft = render(n);
      } else {
        const cidx = dta.indexOf(cls);
        if (cidx !== -1) {
          quote[pp[cidx]] = render(n.childNodes);
        }
      }
      if (n.childNodes) {
        n.childNodes.forEach((e) => {
          visitNode(e);
        });
      }
    };

    visitNode(domNode);

    return (
      <AuthorQuote
        imageCredit={quote.imageCredit}
        imageLeft={quote.imageLeft}
        imageRight={quote.imageRight}
        jobPosition={quote.jobPosition}
        jobPositionDetail={quote.jobPositionDetail}
        name={quote.name}
        text={quote.text}
      />
    );
  };

  const nodeToScriptElement = (domNode) => {
    // allow the script execution browser only
    if (!isClient) {
      return null;
    }
    return <InlineScript {...domNode}></InlineScript>;
  };

  const nodeToMoreDetails = (domNode) => {
    return <MoreDetails data={render(domNode.children)}></MoreDetails>;
  };

  const isFootnotePlaceholder = (domNode) => {
    return domNode.name === 'span' && domNode.attribs && domNode.attribs['data-footnote-placeholder'] === 'true';
  };

  const nodeToFootnoteLink = (domNode) => {
    const footnoteIndex = parseInt(domNode.attribs['data-footnote-index'], 10);

    return <FootnoteLink href={`#footnote-${footnoteIndex}`} index={footnoteIndex} />;
  };

  const extractTextFromNode = (node) => {
    if (!node) return '';
    if (node.name === 'script') {
      return '';
    }
    if (node.type === 'text') {
      return node.data || '';
    }
    if (node.children) {
      return node.children.map((child) => extractTextFromNode(child)).join('');
    }
    return '';
  };

  const injectTimecodeInIframes = (node, startTime) => {
    if (node.name === 'iframe' && node.attribs?.src?.includes('youtube.com/embed/')) {
      const sep = node.attribs.src.includes('?') ? '&' : '?';
      node.attribs.src += `${sep}start=${startTime}`;
    }
    node.children?.forEach((child) => injectTimecodeInIframes(child, startTime));
  };

  const options = {
    replace(domNode) {
      if (domNode.name === 'div' && domNode.attribs?.['data-oembed-url']) {
        const oembedUrl = domNode.attribs['data-oembed-url'];
        if (oembedUrl.includes('youtube') || oembedUrl.includes('youtu.be')) {
          const startTime = parseYoutubeTimecode(oembedUrl);
          if (startTime) {
            injectTimecodeInIframes(domNode, startTime);
          }
        }
      }
      if (domNode.name === 'a' && domNode.attribs && domNode.attribs.target === '_blank') {
        const ariaLabel = domNode.attribs['aria-label'];
        const childText = extractTextFromNode(domNode);
        domNode.attribs.title = `${ariaLabel || childText || t('polylink.no_title')} - ${t('polylink.blank')}`;
      }

      if (isShowMoreDomNode(domNode)) {
        return nodeToMoreDetails(domNode);
      }

      if (isAccordeonDomNode(domNode)) {
        return nodeToInformationDeroulante(domNode);
      }

      if (isLinkBoxSticky(domNode)) {
        return nodeToLinkBoxSticky(domNode);
      }

      if (isEcoWebInlineImage(domNode)) {
        return nodeToEcoWebInlineImage(domNode);
      }
      if (isScript(domNode)) {
        return nodeToScriptElement(domNode);
      }
      if (isAuthorQuote(domNode)) {
        return nodeToAuthorQuote(domNode);
      }
      if (isFootnotePlaceholder(domNode)) {
        return nodeToFootnoteLink(domNode);
      }
    },
  };

  return (
    <div className="ckContent" id={elemAnchor || undefined} lang={getDisplayLang()}>
      {parse(html, options)}
    </div>
  );
};

CkEditor.propTypes = {
  elemAnchor: PropTypes.string,
  html: PropTypes.string.isRequired,
  lang: PropTypes.string,
};

export default CkEditor;
